import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getFavoriteArticles, removeArticleFromFavorites } from '../api/knowledge';
import { toast } from 'sonner';
import CreateTicketModal from '../components/knowledge/CreateTicketModal';
import FloatingHelpButton from '../components/knowledge/FloatingHelpButton';
import EmptyState from '../components/knowledge/EmptyState';
import { ArticleListSkeleton } from '../components/knowledge/LoadingSkeleton';
import SearchBar from '../components/knowledge/SearchBar';
import { Star, Tag, Eye, Plus } from 'lucide-react';
import Button from '../components/ui/button';

const KnowledgeFavorites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketInitial, setTicketInitial] = useState({ subject: '', description: '' });

  const getBasePath = () => {
    if (user?.role === 'admin' || user?.role === 'employee') {
      return '/admin/knowledge';
    }
    return '/customer/knowledge';
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await getFavoriteArticles();
      if (response.success) {
        setFavorites(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch favorite articles:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const openTicketFromArticle = (article) => {
    setTicketInitial({
      subject: article.title,
      description: `User created ticket from favorite article: ${article.title} (ID: ${article.id}).\n\nArticle content: ${article.content || article.short_description || 'N/A'}`,
    });
    setTicketModalOpen(true);
  };

  const handleRemoveFavorite = async (article, e) => {
    e.stopPropagation();
    try {
      const response = await removeArticleFromFavorites(article.id);
      if (response.success) {
        toast.success('Removed from favorites');
        // Optimistically update the list
        setFavorites(prev => prev.filter(fav => fav.id !== article.id));
      } else {
        toast.error(response.message || 'Failed to remove from favorites');
      }
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      toast.error(error.response?.data?.message || 'Failed to remove from favorites');
      // Refresh the list on error to ensure consistency
      fetchFavorites();
    }
  };

  const filteredFavorites = favorites.filter(article => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return article.title.toLowerCase().includes(lower) || 
           (article.content && article.content.toLowerCase().includes(lower)) ||
           (article.short_description && article.short_description.toLowerCase().includes(lower));
  });

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50/50 dark:bg-slate-950 pb-20">
        
        {/* ================= UNIFIED CANONICAL HEADER ================= */}
        <div className="sticky top-0 z-30 bg-gray-50/50 dark:bg-slate-950 pb-4 pt-6">
          <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
            <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl border border-gray-200/60 dark:border-slate-700/60 shadow-lg shadow-indigo-500/5 dark:shadow-black/20 p-6 md:p-8">
              
              {/* Top Row: Icon + Title + Actions */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 dark:from-pink-600 dark:to-rose-700 flex items-center justify-center shadow-md shadow-pink-500/20">
                    <Star className="w-6 h-6 text-white fill-current" />
                  </div>
                  
                  {/* Title + Subtitle */}
                  <div>
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                      My Favorites
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {favorites.length} saved article{favorites.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(getBasePath())}
                    className="hidden md:flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-xs font-bold hover:opacity-90 transition-all"
                  >
                    Browse Articles
                  </button>
                </div>
              </div>

              {/* Search Input */}
              <div className="mb-0">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-rose-500/10 rounded-full blur opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
                  <div className="relative">
                    <SearchBar
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSearch={() => {}}
                      placeholder="Search favorites..."
                      className="w-full !bg-gray-50 dark:!bg-slate-800 !border !border-gray-200 dark:!border-slate-700 !rounded-full !px-4 !py-3 !text-sm focus:!border-pink-500 dark:focus:!border-pink-500 focus:!ring-2 focus:!ring-pink-500/20 dark:focus:!ring-pink-500/30"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ================= MAIN CONTENT ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-lg p-6 md:p-8">
            
            {loading ? (
              <div className="space-y-4">
                <ArticleListSkeleton count={4} />
              </div>
            ) : filteredFavorites.length === 0 ? (
              /* Empty State */
              <div className="py-16">
                <EmptyState
                  icon={Star}
                  title={searchTerm ? "No matching favorites" : "No favorite articles yet"}
                  description={searchTerm 
                    ? "Try adjusting your search terms"
                    : "Browse the Knowledge Base and mark helpful articles as favorites by clicking the star icon. Your favorites will appear here for quick access."}
                  actionLabel="Browse Articles"
                  onAction={() => navigate(getBasePath())}
                />
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredFavorites.length}</span> favorite article{filteredFavorites.length !== 1 ? 's' : ''}
                    {searchTerm && favorites.length !== filteredFavorites.length && (
                      <span className="text-gray-400"> (filtered from {favorites.length})</span>
                    )}
                  </p>
                </div>

                {/* Article Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredFavorites.map((article) => (
                    <article
                      key={article.id}
                      className="group relative bg-white/90 dark:bg-slate-900/90 backdrop-blur rounded-2xl border border-rose-100 dark:border-rose-900/30 shadow-xl shadow-rose-500/10 hover:shadow-rose-500/20 hover:-translate-y-1 transition-all duration-300 ease-out overflow-hidden"
                    >
                      {/* Card Content */}
                      <div className="p-6">
                        {/* Header: Title + Star */}
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 flex-1 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">
                            {article.title}
                          </h3>
                          <button
                            onClick={(e) => handleRemoveFavorite(article, e)}
                            className="flex-shrink-0 p-2 rounded-lg text-yellow-500 dark:text-yellow-400 bg-yellow-500/10 dark:bg-yellow-500/20 hover:bg-yellow-500/20 dark:hover:bg-yellow-500/30 transition-colors"
                            aria-label="Remove from favorites"
                            title="Remove from favorites"
                          >
                            <Star className="w-5 h-5 fill-current" />
                          </button>
                        </div>

                        {/* Short Description */}
                        {(article.short_description || article.content) && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 leading-relaxed">
                            {article.short_description || article.content}
                          </p>
                        )}

                        {/* Metadata: Category + Views */}
                        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-gray-500 dark:text-gray-400">
                          {article.category_name && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/20 dark:border-rose-500/30 rounded-full font-semibold">
                              <Tag className="w-3 h-3" />
                              <span>{article.category_name}</span>
                            </span>
                          )}
                          {article.views !== undefined && (
                            <span className="inline-flex items-center gap-1">
                              <Eye className="w-3.5 h-3.5" />
                              <span>{article.views || 0} views</span>
                            </span>
                          )}
                        </div>

                        {/* Create Ticket Button */}
                        <Button
                          onClick={() => openTicketFromArticle(article)}
                          variant="unstyled"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl hover:from-rose-600 hover:to-pink-600 shadow-lg shadow-rose-500/25 hover:shadow-rose-500/40 hover:-translate-y-0.5 transition-all duration-300"
                        >
                          <Plus className="w-4 h-4" />
                          Create Ticket
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <CreateTicketModal
          isOpen={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          initialSubject={ticketInitial.subject}
          initialDescription={ticketInitial.description}
        />

        <FloatingHelpButton />
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeFavorites;
